/* ═══════════════════════════════════════════════════════════════════
   RFW COMPETENCIAS — Mock Data
   BLOQUE 4 DE 10 — Competencias RFW
   ═══════════════════════════════════════════════════════════════════ */

export type CompetitionType = 'radio' | 'programa' | 'locutor' | 'club';
export type TournamentType = 'semanal' | 'mensual' | 'anual';
export type CompetitionStatus = 'active' | 'upcoming' | 'ended';
export type VotingType = 'likes' | 'reactions' | 'participation' | 'polls' | 'score';

export const competitionTypeLabels: Record<CompetitionType, string> = {
  radio: 'Radio vs Radio',
  programa: 'Programa vs Programa',
  locutor: 'Locutor vs Locutor',
  club: 'Club vs Club',
};

export const competitionTypeIcons: Record<CompetitionType, string> = {
  radio: 'ri-radio-line',
  programa: 'ri-calendar-check-line',
  locutor: 'ri-mic-line',
  club: 'ri-vip-crown-line',
};

export const tournamentLabels: Record<TournamentType, string> = {
  semanal: 'Torneo Semanal',
  mensual: 'Torneo Mensual',
  anual: 'Torneo Anual',
};

export const tournamentIcons: Record<TournamentType, string> = {
  semanal: 'ri-calendar-2-line',
  mensual: 'ri-calendar-check-line',
  anual: 'ri-calendar-todo-line',
};

export const votingLabels: Record<VotingType, string> = {
  likes: 'Likes',
  reactions: 'Reacciones',
  participation: 'Participación',
  polls: 'Encuestas',
  score: 'RFW Score',
};

export const votingIcons: Record<VotingType, string> = {
  likes: 'ri-heart-line',
  reactions: 'ri-emotion-line',
  participation: 'ri-user-star-line',
  polls: 'ri-bar-chart-line',
  score: 'ri-flashlight-line',
};

/* ─── Competition Participant ─── */
export interface CompetitionParticipant {
  id: number;
  name: string;
  subtitle: string;
  logoUrl: string;
  score: number;
  votes: number;
}

/* ─── Competition Prize ─── */
export interface CompetitionPrize {
  badges: string[];
  title: string;
  trophy: 'gold' | 'silver' | 'bronze';
  extraPoints: number;
}

/* ─── RFW Competition ─── */
export interface RfwCompetition {
  id: number;
  type: CompetitionType;
  tournament: TournamentType;
  name: string;
  description: string;
  status: CompetitionStatus;
  challenger: CompetitionParticipant;
  opponent: CompetitionParticipant;
  winner?: CompetitionParticipant;
  startDate: string;
  endDate: string;
  prize: CompetitionPrize;
  votingType: VotingType;
  progress: number;
  totalVotes: number;
}

/* ─── Hall of Fame Entry ─── */
export interface HallOfFameEntry {
  id: number;
  type: CompetitionType;
  tournament: TournamentType;
  period: string;
  winnerName: string;
  winnerLogoUrl: string;
  winnerSubtitle: string;
  opponentName: string;
  opponentLogoUrl: string;
  finalScore: string;
  date: string;
  trophy: 'gold';
}

/* ─── Tournament Bracket ─── */
export interface TournamentBracket {
  type: CompetitionType;
  tournament: TournamentType;
  label: string;
  rounds: {
    name: string;
    matches: RfwCompetition[];
  }[];
  champion?: CompetitionParticipant;
}

/* ═══════════════════════════════════════════════════════════════════
   ACTIVE COMPETITIONS — 8 batallas en vivo
   ═══════════════════════════════════════════════════════════════════ */

const radioLogoMap: Record<number, string> = {
  1: 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20radio%20station%20logo%20design%20with%20musical%20notes%20and%20frequency%20waves%2C%20clean%20circular%20emblem%20style%2C%20warm%20golden%20and%20white%20color%20palette%2C%20simple%20geometric%20shapes%2C%20professional%20broadcast%20aesthetic%2C%20isolated%20on%20transparent%20background%2C%20high%20contrast%20vector%20style&width=200&height=200&seq=comp-logo-1&orientation=squarish',
  3: 'https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20logo%20with%20guitar%20and%20sombrero%20elements%2C%20circular%20design%20warm%20orange%20and%20green%20colors%2C%20clean%20modern%20broadcast%20style%2C%20festive%20vibrant%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=comp-logo-3&orientation=squarish',
  5: 'https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20with%20electric%20guitar%20and%20lightning%20bolt%2C%20circular%20design%20black%20and%20red%20color%20scheme%2C%20modern%20rock%20broadcast%20emblem%2C%20energetic%20and%20bold%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=comp-logo-5&orientation=squarish',
  7: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20logo%20with%20soccer%20ball%20and%20trophy%20icon%2C%20circular%20design%20dark%20blue%20and%20gold%20color%20scheme%2C%20dynamic%20broadcast%20emblem%2C%20energetic%20sports%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=comp-logo-7&orientation=squarish',
  9: 'https://readdy.ai/api/search-image?query=Pop%20music%20radio%20station%20logo%20with%20headphones%20and%20music%20equalizer%20bars%2C%20circular%20design%20vibrant%20pink%20and%20purple%20colors%2C%20modern%20youthful%20broadcast%20emblem%2C%20energetic%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=comp-logo-9&orientation=squarish',
  12: 'https://readdy.ai/api/search-image?query=Mexican%20banda%20music%20radio%20station%20logo%20with%20trumpet%20and%20accordion%2C%20circular%20design%20red%20white%20and%20green%20colors%2C%20festive%20broadcast%20emblem%2C%20traditional%20Mexican%20vibe%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=comp-logo-12&orientation=squarish',
};

const hostPhotoMap: Record<number, string> = {
  11: 'https://readdy.ai/api/search-image?query=Charismatic%20radio%20host%20portrait%20with%20confident%20expression%2C%20modern%20casual%20style%2C%20warm%20studio%20lighting%2C%20energetic%20personality%2C%20clean%20headshot%20photography%2C%20neutral%20warm%20background%2C%20editorial%20quality&width=200&height=200&seq=comp-host-ricky&orientation=squarish',
  7: 'https://readdy.ai/api/search-image?query=Professional%20radio%20host%20portrait%20with%20warm%20genuine%20smile%2C%20smart%20casual%20attire%2C%20clean%20studio%20lighting%2C%20approachable%20personality%2C%20headshot%20photography%2C%20neutral%20cream%20background%2C%20editorial%20quality&width=200&height=200&seq=comp-host-diego&orientation=squarish',
  2: 'https://readdy.ai/api/search-image?query=Beautiful%20latina%20radio%20host%20portrait%20with%20warm%20confident%20smile%2C%20elegant%20casual%20style%2C%20soft%20studio%20lighting%2C%20charismatic%20personality%2C%20headshot%20photography%2C%20neutral%20warm%20background%2C%20editorial%20quality&width=200&height=200&seq=comp-host-vale&orientation=squarish',
  3: 'https://readdy.ai/api/search-image?query=Experienced%20radio%20host%20portrait%20with%20distinguished%20look%2C%20salt%20and%20pepper%20hair%2C%20warm%20confident%20smile%2C%20professional%20casual%20style%2C%20rich%20studio%20lighting%2C%20headshot%20photography%2C%20neutral%20dark%20background%2C%20editorial%20quality&width=200&height=200&seq=comp-host-beto&orientation=squarish',
};

const programCoverMap: Record<number, string> = {
  2: 'https://readdy.ai/api/search-image?query=Talk%20show%20radio%20program%20cover%20art%20with%20warm%20sunset%20colors%20and%20microphone%2C%20modern%20minimalist%20design%2C%20peach%20and%20coral%20tones%2C%20clean%20aesthetic%2C%20professional%20broadcast%20style%2C%20warm%20inviting%20feel&width=200&height=200&seq=comp-prog-tardes&orientation=squarish',
  11: 'https://readdy.ai/api/search-image?query=Fun%20casual%20radio%20program%20cover%20art%20with%20playful%20cartoon%20elements%2C%20bright%20energetic%20colors%20orange%20and%20teal%2C%20young%20modern%20style%2C%20clean%20minimalist%20design%2C%20vibrant%20youthful%20feel&width=200&height=200&seq=comp-prog-desparche&orientation=squarish',
  7: 'https://readdy.ai/api/search-image?query=Creative%20arts%20radio%20program%20cover%20art%20with%20paintbrush%20and%20lightbulb%2C%20colorful%20warm%20gradient%2C%20artistic%20inspiration%20design%2C%20clean%20modern%20style%2C%20creative%20energy%20feel&width=200&height=200&seq=comp-prog-creativo&orientation=squarish',
};

const clubThumbMap: Record<number, string> = {
  3: 'https://readdy.ai/api/search-image?query=Vibrant%20mexican%20music%20fan%20club%20celebration%20with%20colorful%20papel%20picado%20decorations%2C%20festive%20atmosphere%20with%20warm%20amber%20and%20orange%20tones%2C%20traditional%20and%20modern%20fusion%20aesthetic%2C%20energetic%20community%20party%20vibe%2C%20editorial%20event%20photography&width=200&height=200&seq=comp-club-mex&orientation=squarish',
  12: 'https://readdy.ai/api/search-image?query=Massive%20mexican%20banda%20music%20fan%20celebration%20with%20festive%20energy%2C%20traditional%20instruments%20silhouette%2C%20vibrant%20warm%20red%20orange%20and%20green%20tones%2C%20crowd%20energy%20at%20concert%2C%20editorial%20event%20photography&width=200&height=200&seq=comp-club-banda&orientation=squarish',
  7: 'https://readdy.ai/api/search-image?query=Passionate%20sports%20fans%20club%20atmosphere%20with%20stadium%20energy%2C%20people%20cheering%20in%20team%20colors%2C%20warm%20golden%20stadium%20floodlights%2C%20dynamic%20sports%20community%20vibe%2C%20editorial%20sports%20photography&width=200&height=200&seq=comp-club-dep&orientation=squarish',
  5: 'https://readdy.ai/api/search-image?query=Electric%20rock%20music%20fan%20club%20with%20concert%20atmosphere%2C%20guitar%20silhouettes%20against%20warm%20amber%20stage%20lights%2C%20energetic%20crowd%20vibes%2C%20rock%20and%20roll%20aesthetic%2C%20editorial%20concert%20photography&width=200&height=200&seq=comp-club-rock&orientation=squarish',
};

export const activeCompetitions: RfwCompetition[] = [
  /* ─── RADIO VS RADIO ─── */
  {
    id: 1,
    type: 'radio',
    tournament: 'semanal',
    name: 'Batalla de las Californias',
    description: 'Las dos potencias de California se enfrentan por la supremacía estatal. Pop mexicano vs Rock clásico. ¿Quién tiene la comunidad más fuerte?',
    status: 'active',
    challenger: { id: 1, name: 'Radio Nueva Actitud', subtitle: '94.70 RFW · Santa Rosa, CA', logoUrl: radioLogoMap[1], score: 21500, votes: 3450 },
    opponent: { id: 5, name: 'Rock Total Radio', subtitle: '87.01 RFW · Los Ángeles, CA', logoUrl: radioLogoMap[5], score: 24200, votes: 3890 },
    startDate: '2026-06-16',
    endDate: '2026-06-23',
    prize: { badges: ['Campeón Semanal', 'Rey de California'], title: 'Campeón Semanal de Radio', trophy: 'gold', extraPoints: 250 },
    votingType: 'score',
    progress: 68,
    totalVotes: 7340,
  },
  {
    id: 2,
    type: 'radio',
    tournament: 'semanal',
    name: 'Duelo Texano',
    description: 'Texas se parte en dos. Regional Mexicana vs Deportes. La Mexicana FM y Deportes Total Radio miden fuerzas en el torneo más picante de la semana.',
    status: 'active',
    challenger: { id: 3, name: 'La Mexicana FM', subtitle: '103.50 RFW · Houston, TX', logoUrl: radioLogoMap[3], score: 27600, votes: 5620 },
    opponent: { id: 7, name: 'Deportes Total Radio', subtitle: '120.00 RFW · Dallas, TX', logoUrl: radioLogoMap[7], score: 26100, votes: 4780 },
    startDate: '2026-06-16',
    endDate: '2026-06-23',
    prize: { badges: ['Campeón Semanal', 'Rey de Texas'], title: 'Campeón Semanal de Radio', trophy: 'gold', extraPoints: 250 },
    votingType: 'score',
    progress: 72,
    totalVotes: 10400,
  },
  {
    id: 3,
    type: 'radio',
    tournament: 'semanal',
    name: 'Choque de Potencias Emergentes',
    description: 'Banda Radio MX vs La Kalle 99.9. Dos radios que están explotando en crecimiento. La que más suba esta semana se lleva el premio.',
    status: 'active',
    challenger: { id: 12, name: 'Banda Radio MX', subtitle: '200.00 RFW · San Antonio, TX', logoUrl: radioLogoMap[12], score: 28450, votes: 4520 },
    opponent: { id: 9, name: 'La Kalle 99.9', subtitle: '99.90 RFW · Phoenix, AZ', logoUrl: radioLogoMap[9], score: 22800, votes: 2670 },
    startDate: '2026-06-16',
    endDate: '2026-06-23',
    prize: { badges: ['Campeón Semanal', 'Mayor Crecimiento'], title: 'Campeón Semanal de Radio', trophy: 'gold', extraPoints: 250 },
    votingType: 'score',
    progress: 65,
    totalVotes: 7190,
  },
  /* ─── LOCUTOR VS LOCUTOR ─── */
  {
    id: 10,
    type: 'locutor',
    tournament: 'semanal',
    name: 'Duelo de Titanes del Micrófono',
    description: 'Ricky Navarro vs Diego Quintero. Los dos locutores más explosivos del momento frente a frente. Quien reciba más interacciones esta semana gana.',
    status: 'active',
    challenger: { id: 11, name: 'Ricky Navarro', subtitle: 'Locutor · Banda Radio MX', logoUrl: hostPhotoMap[11], score: 18200, votes: 2140 },
    opponent: { id: 7, name: 'Diego Quintero', subtitle: 'Locutor · Deportes Total Radio', logoUrl: hostPhotoMap[7], score: 16800, votes: 1890 },
    startDate: '2026-06-16',
    endDate: '2026-06-23',
    prize: { badges: ['Voz de la Semana', 'Micrófono de Oro'], title: 'Locutor Campeón Semanal', trophy: 'gold', extraPoints: 200 },
    votingType: 'likes',
    progress: 58,
    totalVotes: 4030,
  },
  {
    id: 11,
    type: 'locutor',
    tournament: 'semanal',
    name: 'Experiencia vs Juventud',
    description: 'Beto Fuentes (la vieja escuela) vs Valentina Ríos (la nueva generación). ¿Pesa más la trayectoria o la energía fresca?',
    status: 'active',
    challenger: { id: 3, name: 'Beto Fuentes', subtitle: 'Locutor · Rock Total Radio', logoUrl: hostPhotoMap[3], score: 14600, votes: 1240 },
    opponent: { id: 2, name: 'Valentina Ríos', subtitle: 'Locutora · Radio Nueva Actitud', logoUrl: hostPhotoMap[2], score: 15400, votes: 1520 },
    startDate: '2026-06-16',
    endDate: '2026-06-23',
    prize: { badges: ['Voz de la Semana', 'Micrófono de Plata'], title: 'Locutor Campeón Semanal', trophy: 'gold', extraPoints: 200 },
    votingType: 'likes',
    progress: 52,
    totalVotes: 2760,
  },
  /* ─── PROGRAMA VS PROGRAMA ─── */
  {
    id: 20,
    type: 'programa',
    tournament: 'semanal',
    name: 'La Guerra del Rating',
    description: 'Tardes con Vale vs El Desparche. Los dos programas más escuchados compiten por el trono del engagement. Votá por tu favorito.',
    status: 'active',
    challenger: { id: 2, name: 'Tardes con Vale', subtitle: 'Programa · Radio Nueva Actitud', logoUrl: programCoverMap[2], score: 21500, votes: 3260 },
    opponent: { id: 11, name: 'El Desparche', subtitle: 'Programa · La Mexicana FM', logoUrl: programCoverMap[11], score: 20800, votes: 2980 },
    startDate: '2026-06-16',
    endDate: '2026-06-23',
    prize: { badges: ['Programa Estrella', 'Rey del Rating'], title: 'Programa Campeón Semanal', trophy: 'gold', extraPoints: 200 },
    votingType: 'polls',
    progress: 70,
    totalVotes: 6240,
  },
  /* ─── CLUB VS CLUB ─── */
  {
    id: 30,
    type: 'club',
    tournament: 'semanal',
    name: 'Batalla de Ejércitos',
    description: 'Banda MX Army vs La Mexicana Fan Club. Los dos clubes más grandes de música regional mexicana frente a frente. Gana el que más miembros nuevos sume.',
    status: 'active',
    challenger: { id: 12, name: 'Banda MX Army', subtitle: 'Club · Banda Radio MX · 14.5K miembros', logoUrl: clubThumbMap[12], score: 20500, votes: 980 },
    opponent: { id: 3, name: 'La Mexicana Fan Club', subtitle: 'Club · La Mexicana FM · 18.9K miembros', logoUrl: clubThumbMap[3], score: 22800, votes: 1150 },
    startDate: '2026-06-16',
    endDate: '2026-06-23',
    prize: { badges: ['Club Campeón', 'Ejército Supremo'], title: 'Club Campeón Semanal', trophy: 'gold', extraPoints: 150 },
    votingType: 'participation',
    progress: 60,
    totalVotes: 2130,
  },
  {
    id: 31,
    type: 'club',
    tournament: 'semanal',
    name: 'Fanáticos en Guerra',
    description: 'Deportes Total Fanáticos vs Rock Total Legion. Pasión deportiva contra energía rockera. ¿Qué comunidad tiene más garra?',
    status: 'active',
    challenger: { id: 7, name: 'Deportes Total Fanáticos', subtitle: 'Club · Deportes Total · 15.6K miembros', logoUrl: clubThumbMap[7], score: 19800, votes: 870 },
    opponent: { id: 5, name: 'Rock Total Legion', subtitle: 'Club · Rock Total · 11.2K miembros', logoUrl: clubThumbMap[5], score: 16200, votes: 740 },
    startDate: '2026-06-16',
    endDate: '2026-06-23',
    prize: { badges: ['Club Campeón', 'Comunidad de Fuego'], title: 'Club Campeón Semanal', trophy: 'gold', extraPoints: 150 },
    votingType: 'participation',
    progress: 55,
    totalVotes: 1610,
  },
];

/* ═══════════════════════════════════════════════════════════════════
   UPCOMING COMPETITIONS — 6 próximas batallas
   ═══════════════════════════════════════════════════════════════════ */

export const upcomingCompetitions: RfwCompetition[] = [
  {
    id: 4,
    type: 'radio',
    tournament: 'semanal',
    name: 'Batalla del Sur',
    description: 'Radio Fe y Esperanza vs Radio Vida Nueva. Las dos radios cristianas más grandes de Florida miden su fe y su comunidad.',
    status: 'upcoming',
    challenger: { id: 2, name: 'Radio Fe y Esperanza', subtitle: '87.00 RFW · Miami, FL', logoUrl: 'https://readdy.ai/api/search-image?query=Christian%20radio%20station%20logo%20with%20dove%20symbol%2C%20circular%20minimalist%20design%2C%20soft%20warm%20colors%20white%20and%20gentle%20golden%20tones%2C%20clean%20modern%20broadcast%20emblem%2C%20spiritual%20community%20radio%2C%20isolated%20on%20transparent%20background&width=200&height=200&seq=comp-logo-fe&orientation=squarish', score: 0, votes: 0 },
    opponent: { id: 11, name: 'Radio Vida Nueva', subtitle: '180.00 RFW · Orlando, FL', logoUrl: 'https://readdy.ai/api/search-image?query=Christian%20worship%20radio%20station%20logo%20with%20cross%20and%20sunrise%20symbols%2C%20circular%20design%20soft%20blue%20and%20white%20colors%2C%20peaceful%20spiritual%20broadcast%20emblem%2C%20minimalist%20style%2C%20isolated%20on%20transparent%20background&width=200&height=200&seq=comp-logo-vn&orientation=squarish', score: 0, votes: 0 },
    startDate: '2026-06-23',
    endDate: '2026-06-30',
    prize: { badges: ['Campeón Semanal', 'Rey de Florida'], title: 'Campeón Semanal de Radio', trophy: 'gold', extraPoints: 250 },
    votingType: 'score',
    progress: 0,
    totalVotes: 0,
  },
  {
    id: 5,
    type: 'radio',
    tournament: 'mensual',
    name: 'Gran Final Mensual: Radio del Mes',
    description: 'Los ganadores semanales de junio se enfrentan por el título supremo del mes. La batalla más esperada del calendario RFW.',
    status: 'upcoming',
    challenger: { id: 12, name: 'Banda Radio MX', subtitle: '200.00 RFW · #1 Ranking RFW', logoUrl: radioLogoMap[12], score: 0, votes: 0 },
    opponent: { id: 3, name: 'La Mexicana FM', subtitle: '103.50 RFW · #2 Ranking RFW', logoUrl: radioLogoMap[3], score: 0, votes: 0 },
    startDate: '2026-06-30',
    endDate: '2026-07-07',
    prize: { badges: ['Radio del Mes', 'Corona Mensual', 'Trofeo de Platino'], title: 'Radio del Mes', trophy: 'gold', extraPoints: 500 },
    votingType: 'score',
    progress: 0,
    totalVotes: 0,
  },
  {
    id: 12,
    type: 'locutor',
    tournament: 'mensual',
    name: 'Final Mensual de Locutores',
    description: 'Los mejores locutores de junio se enfrentan en la batalla decisiva. El ganador recibe el Micrófono de Platino y 500 puntos extra.',
    status: 'upcoming',
    challenger: { id: 11, name: 'Ricky Navarro', subtitle: 'Locutor · #1 Host Score', logoUrl: hostPhotoMap[11], score: 0, votes: 0 },
    opponent: { id: 7, name: 'Diego Quintero', subtitle: 'Locutor · #2 Host Score', logoUrl: hostPhotoMap[7], score: 0, votes: 0 },
    startDate: '2026-06-28',
    endDate: '2026-07-05',
    prize: { badges: ['Locutor del Mes', 'Micrófono de Platino'], title: 'Locutor del Mes', trophy: 'gold', extraPoints: 500 },
    votingType: 'likes',
    progress: 0,
    totalVotes: 0,
  },
  {
    id: 21,
    type: 'programa',
    tournament: 'mensual',
    name: 'Final Mensual de Programas',
    description: 'Los programas más votados del mes compiten por el título máximo. Tardes con Vale vs El Desparche en la revancha.',
    status: 'upcoming',
    challenger: { id: 2, name: 'Tardes con Vale', subtitle: 'Programa · #1 Program Score', logoUrl: programCoverMap[2], score: 0, votes: 0 },
    opponent: { id: 11, name: 'El Desparche', subtitle: 'Programa · #2 Program Score', logoUrl: programCoverMap[11], score: 0, votes: 0 },
    startDate: '2026-06-29',
    endDate: '2026-07-06',
    prize: { badges: ['Programa del Mes', 'Rating de Platino'], title: 'Programa del Mes', trophy: 'gold', extraPoints: 500 },
    votingType: 'polls',
    progress: 0,
    totalVotes: 0,
  },
  {
    id: 32,
    type: 'club',
    tournament: 'mensual',
    name: 'Superclásico de Clubes',
    description: 'La gran final mensual de clubes. Los ejércitos más poderosos de RFW se miden en la batalla definitiva por el trono.',
    status: 'upcoming',
    challenger: { id: 3, name: 'La Mexicana Fan Club', subtitle: 'Club · 18.9K miembros · #1 Club Score', logoUrl: clubThumbMap[3], score: 0, votes: 0 },
    opponent: { id: 12, name: 'Banda MX Army', subtitle: 'Club · 14.5K miembros · #2 Club Score', logoUrl: clubThumbMap[12], score: 0, votes: 0 },
    startDate: '2026-06-27',
    endDate: '2026-07-04',
    prize: { badges: ['Club Supremo', 'Trono de la Comunidad'], title: 'Club del Mes', trophy: 'gold', extraPoints: 500 },
    votingType: 'participation',
    progress: 0,
    totalVotes: 0,
  },
  {
    id: 100,
    type: 'radio',
    tournament: 'anual',
    name: 'Gran Torneo Anual RFW 2026',
    description: 'El torneo más prestigioso del año. 12 radios, eliminación directa. Solo una se corona como la Radio del Año. Premio mayor: 2000 puntos RFW Score + Trofeo de Diamante.',
    status: 'upcoming',
    challenger: { id: 0, name: '—', subtitle: 'Por definir', logoUrl: '', score: 0, votes: 0 },
    opponent: { id: 0, name: '—', subtitle: 'Por definir', logoUrl: '', score: 0, votes: 0 },
    startDate: '2026-07-15',
    endDate: '2026-08-15',
    prize: { badges: ['Radio del Año', 'Trofeo Diamante', 'Salón de la Fama'], title: 'Radio del Año 2026', trophy: 'gold', extraPoints: 2000 },
    votingType: 'score',
    progress: 0,
    totalVotes: 0,
  },
];

/* ═══════════════════════════════════════════════════════════════════
   RECENT WINNERS — 6 ganadores recientes
   ═══════════════════════════════════════════════════════════════════ */

export const recentWinners: RfwCompetition[] = [
  {
    id: 50,
    type: 'radio',
    tournament: 'semanal',
    name: 'Batalla del Norte',
    description: '',
    status: 'ended',
    challenger: { id: 7, name: 'Deportes Total Radio', subtitle: '120.00 RFW · Dallas, TX', logoUrl: radioLogoMap[7], score: 26100, votes: 5200 },
    opponent: { id: 6, name: 'Radio Comunitaria Unida', subtitle: '87.02 RFW · Chicago, IL', logoUrl: 'https://readdy.ai/api/search-image?query=Community%20radio%20station%20logo%20with%20people%20holding%20hands%20in%20circle%2C%20minimalist%20design%20with%20warm%20earth%20tones%2C%20green%20and%20orange%20colors%2C%20inclusive%20broadcast%20emblem&width=200&height=200&seq=comp-logo-com&orientation=squarish', score: 16800, votes: 2100 },
    winner: { id: 7, name: 'Deportes Total Radio', subtitle: '120.00 RFW · Dallas, TX', logoUrl: radioLogoMap[7], score: 26100, votes: 5200 },
    startDate: '2026-06-09',
    endDate: '2026-06-16',
    prize: { badges: ['Campeón Semanal', 'Rey del Norte'], title: 'Campeón Semanal de Radio', trophy: 'gold', extraPoints: 250 },
    votingType: 'score',
    progress: 100,
    totalVotes: 7300,
  },
  {
    id: 51,
    type: 'radio',
    tournament: 'semanal',
    name: 'Duelo de Gigantes Latinos',
    description: '',
    status: 'ended',
    challenger: { id: 3, name: 'La Mexicana FM', subtitle: '103.50 RFW · Houston, TX', logoUrl: radioLogoMap[3], score: 27600, votes: 5800 },
    opponent: { id: 1, name: 'Radio Nueva Actitud', subtitle: '94.70 RFW · Santa Rosa, CA', logoUrl: radioLogoMap[1], score: 21500, votes: 3100 },
    winner: { id: 3, name: 'La Mexicana FM', subtitle: '103.50 RFW · Houston, TX', logoUrl: radioLogoMap[3], score: 27600, votes: 5800 },
    startDate: '2026-06-09',
    endDate: '2026-06-16',
    prize: { badges: ['Campeón Semanal', 'Gigante Latino'], title: 'Campeón Semanal de Radio', trophy: 'gold', extraPoints: 250 },
    votingType: 'score',
    progress: 100,
    totalVotes: 8900,
  },
  {
    id: 52,
    type: 'locutor',
    tournament: 'semanal',
    name: 'Choque de Estilos',
    description: '',
    status: 'ended',
    challenger: { id: 7, name: 'Diego Quintero', subtitle: 'Locutor · Deportes Total Radio', logoUrl: hostPhotoMap[7], score: 16800, votes: 1950 },
    opponent: { id: 1, name: 'Martín Castellanos', subtitle: 'Locutor · Radio Nueva Actitud', logoUrl: 'https://readdy.ai/api/search-image?query=Young%20modern%20radio%20host%20portrait%20with%20fresh%20energetic%20look%2C%20casual%20streetwear%20style%2C%20bright%20studio%20lighting%2C%20dynamic%20personality%2C%20headshot%20photography%2C%20neutral%20white%20background&width=200&height=200&seq=comp-host-martin&orientation=squarish', score: 13800, votes: 1200 },
    winner: { id: 7, name: 'Diego Quintero', subtitle: 'Locutor · Deportes Total Radio', logoUrl: hostPhotoMap[7], score: 16800, votes: 1950 },
    startDate: '2026-06-09',
    endDate: '2026-06-16',
    prize: { badges: ['Voz de la Semana', 'Micrófono de Oro'], title: 'Locutor Campeón Semanal', trophy: 'gold', extraPoints: 200 },
    votingType: 'likes',
    progress: 100,
    totalVotes: 3150,
  },
  {
    id: 53,
    type: 'programa',
    tournament: 'semanal',
    name: 'Batalla de Contenido',
    description: '',
    status: 'ended',
    challenger: { id: 7, name: 'Modo Creativo', subtitle: 'Programa · Radio Comunitaria Unida', logoUrl: programCoverMap[7], score: 19600, votes: 2340 },
    opponent: { id: 1, name: 'Mañanas Argentinas', subtitle: 'Programa · Radio Nueva Actitud', logoUrl: 'https://readdy.ai/api/search-image?query=Morning%20radio%20show%20cover%20art%20with%20sunrise%20and%20coffee%20cup%20icon%2C%20warm%20amber%20and%20cream%20colors%2C%20cozy%20morning%20broadcast%20design%2C%20clean%20minimalist%20style&width=200&height=200&seq=comp-prog-mananas&orientation=squarish', score: 16800, votes: 1560 },
    winner: { id: 7, name: 'Modo Creativo', subtitle: 'Programa · Radio Comunitaria Unida', logoUrl: programCoverMap[7], score: 19600, votes: 2340 },
    startDate: '2026-06-09',
    endDate: '2026-06-16',
    prize: { badges: ['Programa Estrella', 'Contenido de Oro'], title: 'Programa Campeón Semanal', trophy: 'gold', extraPoints: 200 },
    votingType: 'polls',
    progress: 100,
    totalVotes: 3900,
  },
  {
    id: 54,
    type: 'club',
    tournament: 'semanal',
    name: 'Duelo de Comunidades',
    description: '',
    status: 'ended',
    challenger: { id: 12, name: 'Banda MX Army', subtitle: 'Club · Banda Radio MX', logoUrl: clubThumbMap[12], score: 20500, votes: 920 },
    opponent: { id: 2, name: 'Comunidad Fe y Esperanza', subtitle: 'Club · Radio Fe y Esperanza', logoUrl: 'https://readdy.ai/api/search-image?query=Warm%20spiritual%20community%20gathering%20with%20soft%20golden%20light%2C%20peaceful%20atmosphere%20of%20people%20praying%20together%2C%20dove%20symbolism%2C%20calm%20and%20serene%20club%20aesthetic&width=200&height=200&seq=comp-club-fe&orientation=squarish', score: 18500, votes: 680 },
    winner: { id: 12, name: 'Banda MX Army', subtitle: 'Club · Banda Radio MX', logoUrl: clubThumbMap[12], score: 20500, votes: 920 },
    startDate: '2026-06-09',
    endDate: '2026-06-16',
    prize: { badges: ['Club Campeón', 'Comunidad Suprema'], title: 'Club Campeón Semanal', trophy: 'gold', extraPoints: 150 },
    votingType: 'participation',
    progress: 100,
    totalVotes: 1600,
  },
  {
    id: 55,
    type: 'radio',
    tournament: 'mensual',
    name: 'Radio del Mes — Mayo 2026',
    description: '',
    status: 'ended',
    challenger: { id: 3, name: 'La Mexicana FM', subtitle: '103.50 RFW · Houston, TX', logoUrl: radioLogoMap[3], score: 27600, votes: 12000 },
    opponent: { id: 7, name: 'Deportes Total Radio', subtitle: '120.00 RFW · Dallas, TX', logoUrl: radioLogoMap[7], score: 26100, votes: 9500 },
    winner: { id: 3, name: 'La Mexicana FM', subtitle: '103.50 RFW · Houston, TX', logoUrl: radioLogoMap[3], score: 27600, votes: 12000 },
    startDate: '2026-05-01',
    endDate: '2026-05-31',
    prize: { badges: ['Radio del Mes', 'Corona Mensual', 'Trofeo de Platino'], title: 'Radio del Mes — Mayo 2026', trophy: 'gold', extraPoints: 500 },
    votingType: 'score',
    progress: 100,
    totalVotes: 21500,
  },
];

/* ═══════════════════════════════════════════════════════════════════
   HALL OF FAME — 12 campeones históricos
   ═══════════════════════════════════════════════════════════════════ */

export const hallOfFame: HallOfFameEntry[] = [
  {
    id: 1, type: 'radio', tournament: 'anual', period: '2025',
    winnerName: 'La Mexicana FM', winnerLogoUrl: radioLogoMap[3], winnerSubtitle: '103.50 RFW · Houston, TX',
    opponentName: 'Deportes Total Radio', opponentLogoUrl: radioLogoMap[7],
    finalScore: '112,400 vs 98,700', date: '2025-12-31', trophy: 'gold',
  },
  {
    id: 2, type: 'radio', tournament: 'mensual', period: 'Mayo 2026',
    winnerName: 'La Mexicana FM', winnerLogoUrl: radioLogoMap[3], winnerSubtitle: '103.50 RFW · Houston, TX',
    opponentName: 'Deportes Total Radio', opponentLogoUrl: radioLogoMap[7],
    finalScore: '27,600 vs 26,100', date: '2026-05-31', trophy: 'gold',
  },
  {
    id: 3, type: 'radio', tournament: 'mensual', period: 'Abril 2026',
    winnerName: 'Banda Radio MX', winnerLogoUrl: radioLogoMap[12], winnerSubtitle: '200.00 RFW · San Antonio, TX',
    opponentName: 'Rock Total Radio', opponentLogoUrl: radioLogoMap[5],
    finalScore: '25,800 vs 22,400', date: '2026-04-30', trophy: 'gold',
  },
  {
    id: 4, type: 'radio', tournament: 'mensual', period: 'Marzo 2026',
    winnerName: 'Deportes Total Radio', winnerLogoUrl: radioLogoMap[7], winnerSubtitle: '120.00 RFW · Dallas, TX',
    opponentName: 'La Mexicana FM', opponentLogoUrl: radioLogoMap[3],
    finalScore: '24,100 vs 23,800', date: '2026-03-31', trophy: 'gold',
  },
  {
    id: 5, type: 'locutor', tournament: 'mensual', period: 'Mayo 2026',
    winnerName: 'Ricky Navarro', winnerLogoUrl: hostPhotoMap[11], winnerSubtitle: 'Locutor · Banda Radio MX',
    opponentName: 'Diego Quintero', opponentLogoUrl: hostPhotoMap[7],
    finalScore: '17,800 vs 16,200', date: '2026-05-31', trophy: 'gold',
  },
  {
    id: 6, type: 'locutor', tournament: 'mensual', period: 'Abril 2026',
    winnerName: 'Valentina Ríos', winnerLogoUrl: hostPhotoMap[2], winnerSubtitle: 'Locutora · Radio Nueva Actitud',
    opponentName: 'Beto Fuentes', opponentLogoUrl: hostPhotoMap[3],
    finalScore: '15,400 vs 14,600', date: '2026-04-30', trophy: 'gold',
  },
  {
    id: 7, type: 'programa', tournament: 'mensual', period: 'Mayo 2026',
    winnerName: 'Tardes con Vale', winnerLogoUrl: programCoverMap[2], winnerSubtitle: 'Programa · Radio Nueva Actitud',
    opponentName: 'El Desparche', opponentLogoUrl: programCoverMap[11],
    finalScore: '21,500 vs 20,800', date: '2026-05-31', trophy: 'gold',
  },
  {
    id: 8, type: 'programa', tournament: 'mensual', period: 'Abril 2026',
    winnerName: 'Modo Creativo', winnerLogoUrl: programCoverMap[7], winnerSubtitle: 'Programa · Radio Comunitaria Unida',
    opponentName: 'Deporte Total', opponentLogoUrl: 'https://readdy.ai/api/search-image?query=Dynamic%20sports%20talk%20program%20cover%20art%20with%20action%20lines%20and%20ball%20icon%2C%20energetic%20navy%20and%20gold%20colors%2C%20modern%20sports%20media%20design%2C%20clean%20professional%20style&width=200&height=200&seq=comp-prog-deporte&orientation=squarish',
    finalScore: '19,600 vs 18,400', date: '2026-04-30', trophy: 'gold',
  },
  {
    id: 9, type: 'club', tournament: 'mensual', period: 'Mayo 2026',
    winnerName: 'Banda MX Army', winnerLogoUrl: clubThumbMap[12], winnerSubtitle: 'Club · Banda Radio MX',
    opponentName: 'La Mexicana Fan Club', opponentLogoUrl: clubThumbMap[3],
    finalScore: '20,500 vs 22,800', date: '2026-05-31', trophy: 'gold',
  },
  {
    id: 10, type: 'club', tournament: 'mensual', period: 'Abril 2026',
    winnerName: 'La Mexicana Fan Club', winnerLogoUrl: clubThumbMap[3], winnerSubtitle: 'Club · La Mexicana FM',
    opponentName: 'Deportes Total Fanáticos', opponentLogoUrl: clubThumbMap[7],
    finalScore: '22,800 vs 19,800', date: '2026-04-30', trophy: 'gold',
  },
  {
    id: 11, type: 'radio', tournament: 'semanal', period: 'Semana 24 · 2026',
    winnerName: 'Banda Radio MX', winnerLogoUrl: radioLogoMap[12], winnerSubtitle: '200.00 RFW · San Antonio, TX',
    opponentName: 'La Mexicana FM', opponentLogoUrl: radioLogoMap[3],
    finalScore: '28,450 vs 27,600', date: '2026-06-16', trophy: 'gold',
  },
  {
    id: 12, type: 'radio', tournament: 'semanal', period: 'Semana 23 · 2026',
    winnerName: 'Deportes Total Radio', winnerLogoUrl: radioLogoMap[7], winnerSubtitle: '120.00 RFW · Dallas, TX',
    opponentName: 'Radio Comunitaria Unida', opponentLogoUrl: 'https://readdy.ai/api/search-image?query=Community%20radio%20station%20logo%20with%20people%20holding%20hands%20in%20circle%2C%20minimalist%20design%20with%20warm%20earth%20tones%2C%20green%20and%20orange%20colors%2C%20inclusive%20broadcast%20emblem&width=200&height=200&seq=comp-hof-com&orientation=squarish',
    finalScore: '26,100 vs 16,800', date: '2026-06-09', trophy: 'gold',
  },
];

/* ═══════════════════════════════════════════════════════════════════
   RECORDS — Récords históricos
   ═══════════════════════════════════════════════════════════════════ */

export interface CompetitionRecord {
  label: string;
  holder: string;
  holderLogoUrl: string;
  value: string;
  icon: string;
  color: string;
}

export const competitionRecords: CompetitionRecord[] = [
  {
    label: 'Mayor puntuación en una batalla',
    holder: 'La Mexicana FM', holderLogoUrl: radioLogoMap[3],
    value: '27,600 pts', icon: 'ri-flashlight-fill', color: 'text-amber-600 bg-amber-100',
  },
  {
    label: 'Más victorias consecutivas',
    holder: 'Banda Radio MX', holderLogoUrl: radioLogoMap[12],
    value: '5 victorias', icon: 'ri-medal-line', color: 'text-yellow-600 bg-yellow-100',
  },
  {
    label: 'Batalla con más votos',
    holder: 'La Mexicana FM vs Deportes Total', holderLogoUrl: radioLogoMap[3],
    value: '21,500 votos', icon: 'ri-bar-chart-line', color: 'text-rose-600 bg-rose-100',
  },
  {
    label: 'Club con más títulos',
    holder: 'Banda MX Army', holderLogoUrl: clubThumbMap[12],
    value: '3 títulos', icon: 'ri-vip-crown-line', color: 'text-purple-600 bg-purple-100',
  },
  {
    label: 'Locutor más dominante',
    holder: 'Ricky Navarro', holderLogoUrl: hostPhotoMap[11],
    value: '85% victorias', icon: 'ri-mic-line', color: 'text-emerald-600 bg-emerald-100',
  },
  {
    label: 'Programa más laureado',
    holder: 'Tardes con Vale', holderLogoUrl: programCoverMap[2],
    value: '4 títulos', icon: 'ri-calendar-check-line', color: 'text-sky-600 bg-sky-100',
  },
];

/* ═══════════════════════════════════════════════════════════════════
   HELPER FUNCTIONS
   ═══════════════════════════════════════════════════════════════════ */

export function getActiveCompetitions(): RfwCompetition[] {
  return activeCompetitions;
}

export function getUpcomingCompetitions(): RfwCompetition[] {
  return upcomingCompetitions;
}

export function getRecentWinners(): RfwCompetition[] {
  return recentWinners;
}

export function getHallOfFame(): HallOfFameEntry[] {
  return hallOfFame;
}

export function getCompetitionsByType(type: CompetitionType): RfwCompetition[] {
  return [
    ...activeCompetitions,
    ...upcomingCompetitions,
    ...recentWinners,
  ].filter(c => c.type === type);
}

export function getRecords(): CompetitionRecord[] {
  return competitionRecords;
}

export function formatCompetitionDates(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  return `${s.getDate()} ${months[s.getMonth()]} — ${e.getDate()} ${months[e.getMonth()]}, ${e.getFullYear()}`;
}

export function getTimeRemaining(endDate: string): string {
  const now = new Date();
  const end = new Date(endDate);
  const diff = end.getTime() - now.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  if (days > 0) return `${days}d ${hours}h restantes`;
  return `${hours}h restantes`;
}

export function getCompetitionPercent(challengerVotes: number, opponentVotes: number): { challenger: number; opponent: number } {
  const total = challengerVotes + opponentVotes;
  if (total === 0) return { challenger: 50, opponent: 50 };
  return {
    challenger: Math.round((challengerVotes / total) * 100),
    opponent: Math.round((opponentVotes / total) * 100),
  };
}